import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import PageHeader from "@/components/landing/PageHeader";
import SEO from "@/components/SEO";
import usePageTitle from "@/hooks/usePageTitle";

export default function TermsPage() {
  usePageTitle("Terms of Use");
  return (
    <div className="bg-[var(--sci-bg)]">
      <SEO
        title="Terms of Use"
        description="Terms governing the use of International Sci Ink Press websites, submission portals, and editorial content. CC-BY 4.0 open access licensing."
        path="/terms"
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "Terms of Use", url: "/terms" },
        ]}
      />
      <Navbar />
      <PageHeader
        overline="Terms of Use"
        title={
          <>
            Editorial integrity, <em className="not-italic text-[var(--sci-teal)]">defined</em>.
          </>
        }
        sub="The terms governing your use of the International Sci Ink Press publisher website and the journal portals operated by us."
      />
      <section className="py-20 md:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Block title="1. Acceptance">
            <p>
              By accessing the websites operated by International Sci Ink Press Ltd you agree to these terms. If you do not agree, please discontinue use.
            </p>
          </Block>
          <Block title="2. Editorial content & open access">
            <p>
              Articles published in our journals are released under the Creative Commons Attribution 4.0 International (CC-BY 4.0) license unless explicitly stated otherwise. Authors retain copyright in their work; users may share and adapt the material with appropriate attribution.
            </p>
          </Block>
          <Block title="3. Acceptable use">
            <ul>
              <li>Do not attempt to disrupt, attack, or reverse-engineer this site or its submission portals.</li>
              <li>Automated scraping is permitted only where it complies with our robots policy and Crossref's licensing terms for metadata.</li>
              <li>Misrepresentation of authorship, plagiarism, or duplicate submission may result in retraction and disqualification of future submissions per COPE guidelines.</li>
            </ul>
          </Block>
          <Block title="4. Submission rules">
            <p>
              All submissions are handled through the journal-specific eJManager portals. By submitting a manuscript you confirm that the work is original, not under consideration elsewhere, that all named authors approve submission, and that you have declared any conflicts of interest as required by ICMJE.
            </p>
          </Block>
          <Block title="5. External links">
            <p>
              We link to third-party sites (Crossref, Portico, ORCID, EQUATOR Network, cOAlition S, Nutramilo, eJManager) for editorial transparency. We do not control and are not responsible for the content of those sites.
            </p>
          </Block>
          <Block title="6. Limitation of liability">
            <p>
              The publisher site is provided "as is". Sci Ink Press Ltd is not liable for incidental damages arising from the use of this site to the extent permitted by Bulgarian and EU law.
            </p>
          </Block>
          <Block title="7. Governing law">
            <p>
              These terms are governed by the laws of the Republic of Bulgaria. Disputes shall be resolved before the competent courts of Sofia, Bulgaria.
            </p>
          </Block>
          <Block title="8. Contact">
            <p>
              Editorial office: through the journal portals at{" "}
              <a href="https://www.mdpub.net" target="_blank" rel="noopener noreferrer" className="text-[var(--sci-teal)] underline">mdpub.net</a>{" "}and{" "}
              <a href="https://www.ejos.org" target="_blank" rel="noopener noreferrer" className="text-[var(--sci-teal)] underline">ejos.org</a>.
            </p>
            <p className="font-mono-sci text-xs tracking-wider text-[var(--sci-text-muted)] mt-4">
              LAST UPDATED · JUNE 2026
            </p>
          </Block>
        </div>
      </section>
      <Footer />
    </div>
  );
}

function Block({ title, children }) {
  return (
    <section className="mb-12">
      <h2 className="font-serif-display text-2xl md:text-3xl text-[var(--sci-navy)] tracking-tight mb-4">
        {title}
      </h2>
      <div className="text-[var(--sci-text)]/85 leading-relaxed space-y-3 [&_a]:text-[var(--sci-teal)] [&_a]:underline [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-2">
        {children}
      </div>
    </section>
  );
}
